import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import GoogleSignInButton from './GoogleSignInButton';
import pendingActionService from '../services/pendingActionService';
import { trackLandingView, trackCTAClick, trackSignupStart, trackSignupComplete, trackLogin } from '../utils/analytics';
import { getPostAuthRedirect } from '../utils/authRedirect';
import SEO from './SEO';
import PersonaCrossSell from './PersonaCrossSell';
import { PAGE_SEO, buildBreadcrumbs } from '../utils/seoConfig';
import './PersonaLanding.css';
import './FitnessLanding.css';

const FitnessLanding = () => {
  const navigate = useNavigate();
  const [authError, setAuthError] = useState(null);

  useEffect(() => { trackLandingView('fitness'); }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    trackSignupStart('fitness');
    try {
      if (!credentialResponse.credential) { setAuthError('Missing authentication credentials'); return; }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential }),
      });
      if (!response.ok) throw new Error(`Authentication failed: ${response.status}`);
      const data = await response.json();
      const userData = { ...data.user, sub: data.user.sub || data.user.id || data.user.email };
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', data.token);
      if (data.isNewUser) {
        trackSignupComplete('fitness', userData.sub || userData.email);
      } else {
        trackLogin('fitness', userData.sub || userData.email);
      }
      const redirectUrl = await pendingActionService.checkAndExecutePendingAction();
      const next = getPostAuthRedirect({
        isNewUser: data.isNewUser,
        pendingRedirectUrl: redirectUrl,
        pendingSubscription: localStorage.getItem('pendingSubscription'),
        defaultHref: '/fitness/exercises'
      });
      if (redirectUrl) toast.success('Welcome!');
      window.location.href = next;
    } catch (error) {
      console.error('Auth error:', error);
      setAuthError(`Login failed: ${error.message}`);
    }
  };

  const seo = PAGE_SEO['/fitness-training'];

  return (
    <div className="persona-landing fitness-training">
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical="/fitness-training"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: seo.title,
            description: seo.description,
            url: 'https://freshfitlab.io/fitness-training',
            about: { '@type': 'Thing', name: 'Fitness Training' },
          },
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'Fitness Training', url: '/fitness-training' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How many exercises does FreshFitLab have?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Over 2,000 exercises spanning strength, cardio, flexibility, plyometrics, mobility, and Olympic lifting. Each has step-by-step instructions, target muscles, equipment, and instructional videos when available.',
                },
              },
              {
                '@type': 'Question',
                name: 'What\u2019s the difference between a workout and a program?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'A workout is a single session with exercises, sets, reps, and rest. A program is a multi-week plan that organizes workouts across days and weeks with progression built in.',
                },
              },
              {
                '@type': 'Question',
                name: 'Can I create my own workouts and programs?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. Logged-in users can build custom workouts and multi-week programs, or clone any public workout/program and customize it.',
                },
              },
            ],
          },
        ]}
      />
      <div className="pl-bg-shapes">
        <div className="pl-shape pl-shape-1"></div>
        <div className="pl-shape pl-shape-2"></div>
        <div className="pl-shape pl-shape-3"></div>
      </div>

      {/* ─── Hero ─── */}
      <section className="pl-hero">
        <span className="pl-hero-badge">Your Training, Your Way</span>
        <h1>
          Follow Programs,<br />
          <span className="pl-highlight">Crush Workouts</span>
        </h1>
        <p className="pl-hero-subtitle">
          Browse structured training programs, follow guided workouts, and track every set and rep —
          whether you're at the gym or at home.
        </p>

        <div className="pl-hero-ctas">
          <button className="pl-cta-primary" onClick={() => { trackCTAClick('fitness', 'hero'); navigate('/fitness/exercises'); }}>
            <span>Explore Exercises</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
          <button className="pl-cta-secondary" onClick={() => { trackCTAClick('fitness', 'hero_secondary'); navigate('/fitness/programs'); }}>
            <span>Browse Programs</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>

        <Link
          to="/pricing#pricing-personal"
          className="pl-pricing-link pl-hero-pricing-link"
          onClick={() => trackCTAClick('fitness', 'hero_pricing')}
        >
          Free to start — plans from $5/month →
        </Link>

        <div className="pl-login-divider"><span>or</span></div>
        <div className="pl-login-section">
          <p className="pl-login-label">Sign in to track your progress</p>
          {process.env.NODE_ENV === 'development' ? (
            <button onClick={() => navigate('/dev/login')} className="pl-dev-login-button">Dev Login</button>
          ) : (
            <GoogleSignInButton onSuccess={handleGoogleSuccess} onError={() => setAuthError('Login Failed')} text="signin" shape="pill" />
          )}
          {authError && <p className="pl-auth-error">{authError}</p>}
        </div>

        <div className="pl-app-badges">
          <a href="https://apps.apple.com/us/app/freshmenulab/id6758483247" className="pl-app-badge" target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store">
            <svg viewBox="0 0 384 512" width="18" height="18" fill="currentColor"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
            <div className="pl-badge-text"><span className="pl-badge-small">Download on the</span><span className="pl-badge-large">App Store</span></div>
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.freshmenulab" className="pl-app-badge" target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play">
            <svg viewBox="0 0 512 512" width="18" height="18" fill="currentColor"><path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/></svg>
            <div className="pl-badge-text"><span className="pl-badge-small">Get it on</span><span className="pl-badge-large">Google Play</span></div>
          </a>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="pl-steps">
        <h2 className="pl-steps-title">How It Works</h2>
        <div className="pl-steps-grid">
          <div className="pl-step">
            <div className="pl-step-number">1</div>
            <h3>Pick a Program</h3>
            <p>Choose from multi-week programs designed for strength, mobility, endurance, and more.</p>
          </div>
          <div className="pl-step">
            <div className="pl-step-number">2</div>
            <h3>Follow Workouts</h3>
            <p>Each session guides you through exercises with sets, reps, and rest timers built in.</p>
          </div>
          <div className="pl-step">
            <div className="pl-step-number">3</div>
            <h3>Track Progress</h3>
            <p>Log every session. See your history, track PRs, and watch your strength grow over time.</p>
          </div>
        </div>
      </section>

      {/* ─── Features ─── */}
      <section className="pl-features">
        <h2 className="pl-features-title">Built for Real Training</h2>
        <div className="pl-features-grid">
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-programs"><i className="fas fa-layer-group"></i></div>
            <h3>Structured Programs</h3>
            <p>Multi-week programs with progressive overload, created by coaches or built by you.</p>
          </div>
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-library"><i className="fas fa-running"></i></div>
            <h3>Exercise Library</h3>
            <p>Hundreds of exercises with descriptions, muscle groups, and equipment tags to browse.</p>
          </div>
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-active"><i className="fas fa-stopwatch"></i></div>
            <h3>Active Workout Tracking</h3>
            <p>Start a session and log sets in real-time. Rest timers keep you on pace.</p>
          </div>
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-schedule"><i className="fas fa-calendar-check"></i></div>
            <h3>Schedule &amp; Log</h3>
            <p>Plan your training week and review past sessions with detailed workout history.</p>
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="pl-final-cta">
        <h2>Ready to take your training seriously?</h2>
        <p>Start following real programs and tracking real progress — for free.</p>
        <div className="pl-final-cta-buttons">
          <button className="pl-cta-primary pl-cta-dark" onClick={() => { trackCTAClick('fitness', 'footer'); navigate('/fitness/exercises'); }}>
            <span>Start Training Free</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
          <button className="pl-cta-secondary" onClick={() => { trackCTAClick('fitness', 'footer_pricing'); navigate('/pricing#pricing-personal'); }} style={{ borderColor: 'rgba(255,255,255,0.25)', color: '#fff', background: 'rgba(255,255,255,0.1)' }}>
            <span>See Plans &amp; Pricing</span>
          </button>
          <a href="https://apps.apple.com/us/app/freshmenulab/id6758483247" className="pl-app-badge pl-app-badge-dark" target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 384 512" width="18" height="18" fill="currentColor"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
            <div className="pl-badge-text"><span className="pl-badge-small">Download on the</span><span className="pl-badge-large">App Store</span></div>
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.freshmenulab" className="pl-app-badge pl-app-badge-dark" target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 512 512" width="18" height="18" fill="currentColor"><path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/></svg>
            <div className="pl-badge-text"><span className="pl-badge-small">Get it on</span><span className="pl-badge-large">Google Play</span></div>
          </a>
        </div>
      </section>

      <PersonaCrossSell current="fitness" />
    </div>
  );
};

export default FitnessLanding;
